import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ShieldCheck, Copy } from 'lucide-react';

// Sign-in steps: password -> (new password) -> (authenticator setup) -> code.
// The server decides the next step; only the last one returns an access token.
const TITLES = {
  password: 'Sign in',
  password_change: 'Set a new password',
  mfa_setup: 'Set up two-step sign-in',
  mfa: 'Enter your code',
  recovery: 'Save your backup codes',
};

export const Login = () => {
  const location = useLocation();
  const [step, setStep] = useState('password');
  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState('');
  const [setup, setSetup] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // The sign-in page is designed light only; Layout re-applies the saved theme after sign-in.
  useEffect(() => { document.documentElement.classList.remove('dark'); }, []);

  const finish = (data) => {
    if (data.recovery_codes?.length) {
      setRecovery(data.recovery_codes);
      setPending(data);
      setStep('recovery');
      return;
    }
    login(data.access_token, data.user);
    toast.success('Welcome back!');
    navigate('/dashboard');
  };

  const handle = (data) => {
    if (data.access_token) { finish(data); return; }
    setChallenge(data.challenge_token);
    setSetup(data.mfa_setup || null);
    setCode('');
    setStep(data.status);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (step === 'password') {
        handle((await api.post('/auth/login', { email, password })).data);
      } else if (step === 'password_change') {
        if (newPassword !== confirm) { toast.error('The two passwords do not match'); return; }
        handle((await api.post('/auth/challenge', { challenge_token: challenge, new_password: newPassword })).data);
      } else {
        handle((await api.post('/auth/challenge', { challenge_token: challenge, code })).data);
      }
    } catch (error) {
      const status = error?.response?.status;
      toast.error(error?.response?.data?.detail || 'Sign-in failed');
      if (status === 401 && step !== 'password') { setStep('password'); setPassword(''); }
    } finally {
      setLoading(false);
    }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(recovery.join('\n'));
    toast.success('Backup codes copied');
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#F7F3EC]">
      {/* Brand side: a real Furniva piece, hidden on phones so the form comes first. */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src="/login-hero.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2A1D15]/85 via-[#2A1D15]/25 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-10 text-[#F7F3EC]">
          <p className="text-xs uppercase tracking-[0.25em] text-[#E9C9A8]">Operations hub</p>
          <p className="mt-3 text-3xl font-semibold font-[Manrope] leading-tight max-w-md">Every order, parcel and promise, in one place.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-5 sm:p-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/login-logo.png" alt="Furniva" className="mx-auto h-14 w-auto mix-blend-multiply" />
          <p className="text-[#7A6555] mt-4 text-sm tracking-wide">CRM · Operations hub</p>
        </div>
        <Card className="border-[#E6DCCD] bg-white shadow-[0_18px_50px_-24px_rgba(59,42,32,0.35)]">
          <CardHeader>
            <CardTitle className="font-[Manrope] flex items-center gap-2">
              {step !== 'password' && <ShieldCheck className="w-5 h-5 text-primary" />}
              {TITLES[step]}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {step === 'recovery' ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  If you lose your phone, each of these codes lets you sign in once. Keep them somewhere safe. They are shown only now.
                </p>
                <div className="grid grid-cols-2 gap-2 font-[JetBrains_Mono] text-sm bg-muted/50 rounded-lg p-3">
                  {recovery.map((c) => <span key={c}>{c}</span>)}
                </div>
                <Button variant="outline" className="w-full" onClick={copyCodes}><Copy className="w-4 h-4 mr-2" />Copy codes</Button>
                <Button className="w-full" onClick={() => finish({ ...pending, recovery_codes: null })}>I've saved them — continue</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                {step === 'password' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input data-testid="login-email-input" id="email" type="email" placeholder="you@furniva.in"
                        value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password">Password</Label>
                        <Link to="/forgot-password" className="text-xs text-primary hover:underline">Forgot password?</Link>
                      </div>
                      <Input data-testid="login-password-input" id="password" type="password" placeholder="••••••••"
                        value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
                    </div>
                  </>
                )}

                {step === 'password_change' && (
                  <>
                    <p className="text-sm text-muted-foreground">
                      For security, choose a new password: at least 12 characters, with upper and lower case letters, a number and a symbol. Passwords are renewed once a year.
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="newpw">New password</Label>
                      <Input id="newpw" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                        required minLength={12} autoComplete="new-password" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmpw">Repeat new password</Label>
                      <Input id="confirmpw" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                        required minLength={12} autoComplete="new-password" />
                    </div>
                  </>
                )}

                {step === 'mfa_setup' && setup && (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      1. Install Google Authenticator or Microsoft Authenticator on your phone.<br />
                      2. In the app, tap <b>+</b> and scan this code.<br />
                      3. Type the 6-digit code the app shows.
                    </p>
                    <img src={setup.qr} alt="Authenticator QR code" className="mx-auto w-48 h-48 rounded border border-border bg-card p-2" />
                    <p className="text-xs text-center text-muted-foreground">
                      Can't scan? Enter this key in the app: <span className="font-[JetBrains_Mono] break-all">{setup.secret}</span>
                    </p>
                  </div>
                )}

                {step === 'mfa' && (
                  <p className="text-sm text-muted-foreground">
                    Open your authenticator app and type the 6-digit code for Furniva CRM. Lost your phone? Type one of your backup codes.
                  </p>
                )}

                {(step === 'mfa_setup' || step === 'mfa') && (
                  <div className="space-y-2">
                    <Label htmlFor="code">Code</Label>
                    <Input id="code" inputMode={step === 'mfa' ? 'text' : 'numeric'} autoComplete="one-time-code" autoFocus
                      placeholder="123456" value={code} onChange={(e) => setCode(e.target.value)} required
                      className="font-[JetBrains_Mono] tracking-widest text-lg" />
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                  {loading ? 'Please wait…' : step === 'password' ? 'Sign in' : 'Continue'}
                </Button>
                {step === 'password' ? (
                  <p className="text-xs text-muted-foreground text-center pt-2">
                    Registration is by invitation only.<br />Contact your admin to get access.
                  </p>
                ) : (
                  <button type="button" className="text-xs text-muted-foreground hover:underline w-full"
                    onClick={() => { setStep('password'); setPassword(''); }}>
                    Start again
                  </button>
                )}
              </form>
            )}
          </CardContent>
        </Card>
        <p className="mt-6 text-center text-xs text-[#9A8676]">© {new Date().getFullYear()} Furniva · Jaipur</p>
      </div>
      </div>
    </div>
  );
};

export default Login;
